import { Canvas } from "@react-three/fiber";
import { Environment } from "@react-three/drei";
import { Suspense, useEffect, useState } from "react";
import { CameraRig } from "./CameraRig";
import { Lighting } from "./Lighting";
import { Postprocessing } from "./Postprocessing";
import { HeroEmblem } from "./sections/HeroEmblem";
import { GoldVault } from "./sections/GoldVault";
import { CryptoCoin } from "./sections/CryptoCoin";
import { Earth } from "./sections/Earth";
import { RobotBrain } from "./sections/RobotBrain";
import { ComputeCore } from "./sections/ComputeCore";
import { DataCenterHall } from "./sections/DataCenterHall";
import { BhutanTribute } from "./sections/BhutanTribute";
import { Constellation } from "./sections/Constellation";

function detectQualityTier() {
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  const isNarrowViewport = typeof window !== "undefined" ? window.innerWidth <= 900 : false;
  const coarsePointer =
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(pointer: coarse)").matches
      : false;
  const saveData = (nav as Navigator & { connection?: { saveData?: boolean } })?.connection?.saveData ?? false;
  const cpu = nav?.hardwareConcurrency ?? 8;
  const memory = (nav as Navigator & { deviceMemory?: number })?.deviceMemory ?? 8;
  const lowPower = saveData || cpu <= 4 || memory <= 4 || isNarrowViewport || coarsePointer;
  return { lowPower };
}

export function QuantumScene() {
  const { lowPower } = detectQualityTier();
  const [isForeground, setIsForeground] = useState(true);

  useEffect(() => {
    const updateActivity = () => {
      const visible = document.visibilityState === "visible";
      const focused = typeof document.hasFocus === "function" ? document.hasFocus() : true;
      setIsForeground(visible && focused);
    };
    updateActivity();
    document.addEventListener("visibilitychange", updateActivity);
    window.addEventListener("focus", updateActivity);
    window.addEventListener("blur", updateActivity);
    return () => {
      document.removeEventListener("visibilitychange", updateActivity);
      window.removeEventListener("focus", updateActivity);
      window.removeEventListener("blur", updateActivity);
    };
  }, []);

  return (
    <div className="scene-root" aria-hidden="true">
      <Canvas
        frameloop={isForeground ? "always" : "never"}
        gl={{
          antialias: !lowPower,
          // Avoid forcing high-performance GPU mode to reduce impact on other apps/tabs.
          powerPreference: "default",
          alpha: false,
        }}
        camera={{ position: [0, 0, 6], fov: 38, near: 0.1, far: 240 }}
        dpr={lowPower ? [0.75, 1] : [0.9, 1.25]}
        shadows={false}
      >
        <color attach="background" args={["#07070a"]} />
        <fog attach="fog" args={["#07070a", 14, 80]} />

        <Suspense fallback={null}>
          <Environment preset="studio" environmentIntensity={0.35} />
          <Lighting />

          <group position={[0, 0, 0]}>
            <HeroEmblem />
          </group>
          <group position={[0, 0, -14]}>
            <GoldVault />
          </group>
          <group position={[0, 0, -28]}>
            <CryptoCoin />
          </group>
          <group position={[0, 0, -42]}>
            <Earth />
          </group>
          <group position={[0, 0, -56]}>
            <RobotBrain />
          </group>
          <group position={[0, 0, -66]}>
            <ComputeCore />
          </group>
          <group position={[0, 0, -72]}>
            <DataCenterHall />
          </group>
          <group position={[0, 0, -88]}>
            <BhutanTribute />
          </group>
          <group position={[0, 0, -82]}>
            <Constellation />
          </group>

          <CameraRig />
          {!lowPower && <Postprocessing />}
        </Suspense>
      </Canvas>
    </div>
  );
}
